import type { ColumnDef } from '@tanstack/react-table';
import styled from 'styled-components';

import { BundleState, ContainerType, GetBundlesFilterQuery } from 'data/__generated__';

import { DetailLink } from 'components/DetailsView/DetailLink';

import { formatDateForDisplay, getExtendedFormattedDatetime } from 'helpers/dateTimes';

import { BundleTypeTag } from 'pages/Bundles/components/TypeTag';
import { ContainerTypeTag } from 'pages/Bundles/components/ContainerTypeTag';
import { StatusTag } from 'pages/Bundles/components/StatusTag';

export type BundleRow = GetBundlesFilterQuery['bundlesFilter']['bundles'][number];

export const CONTAINER_TYPE_LABELS: Record<ContainerType, string> = {
    [ContainerType.Box]: 'Box',
    [ContainerType.Palette]: 'Palette',
    [ContainerType.Kangabox]: 'Kangabox',
    [ContainerType.Parcel]: 'Colis',
    [ContainerType.FreshParcel]: 'Colis frais',
    [ContainerType.Kraft]: 'Kraft',
};

// A bundle can group boxes from several sites — list each distinct site once.
export function getBundleSiteNames(row: BundleRow): string {
    const names = [...new Set(row.boxes.map((box) => box.siteName).filter((name): name is string => !!name))];
    return names.length ? names.join(', ') : '-';
}

/** Une livraison servie par le bundle, et le nombre de ses caisses qui y sont préparées. */
export type BundleDelivery = {
    deliveryId: string;
    name: string;
    numberOfBoxes: number;
};

/**
 * Les livraisons d'un bundle, dédupliquées sur `deliveryId`. Un bundle groupe des caisses de plusieurs
 * livraisons : c'est la livraison, pas la caisse, qui dit POUR QUI on prépare.
 *
 * Le nom suit la nature de la livraison : un site pour une livraison de magasin, l'organisation pour une
 * commande freefood (qui n'a pas de site). `deliveryCode` ne sert que de dernier recours.
 */
export function getBundleDeliveries(row: BundleRow): BundleDelivery[] {
    const deliveriesById = new Map<string, BundleDelivery>();

    for (const { deliveryId, deliveryCode, siteName, organizationName } of row.boxes) {
        const known = deliveriesById.get(deliveryId);
        if (known) {
            known.numberOfBoxes += 1;
            continue;
        }
        deliveriesById.set(deliveryId, {
            deliveryId,
            name: siteName || organizationName || deliveryCode || 'Livraison sans nom',
            numberOfBoxes: 1,
        });
    }

    return [...deliveriesById.values()].sort((first, second) => first.name.localeCompare(second.name));
}

export function formatPreparationDuration(preparationDuration: number | null | undefined): string {
    if (!preparationDuration) {
        return '-';
    }
    return `${Math.floor(preparationDuration / 60)}h ${(preparationDuration % 60).toString().padStart(2, '0')}`;
}

/**
 * Les colonnes du tableau. Elles ne portent plus de filtre : la `DataView` les déclare une fois pour
 * ses deux vues (`constants/bundleFilters.tsx`), et le tri se fait sur les lignes déjà chargées.
 */
export const BUNDLE_TABLE_COLUMNS: ColumnDef<BundleRow, unknown>[] = [
    {
        accessorKey: 'number',
        header: 'N°',
        meta: { defaultVisible: true },
    },
    {
        accessorKey: 'createdAt',
        header: 'Créé le',
        meta: { defaultVisible: true },
        cell: ({ row }) => getExtendedFormattedDatetime(row.original.createdAt),
    },
    {
        // L'accesseur garde la date brute (`YYYY-MM-DD`) : c'est elle qui trie, le rendu n'est que
        // l'affichage français.
        accessorKey: 'scheduledPreparationDate',
        header: 'Préparation prévue',
        meta: { defaultVisible: true },
        cell: ({ row }) => formatDateForDisplay(row.original.scheduledPreparationDate, { displayDay: true }),
    },
    {
        id: 'warehouseColumnName',
        accessorFn: (row) => row.warehouseColumn?.name ?? '-',
        header: 'Entrepôt',
        meta: { defaultVisible: false },
    },
    {
        id: 'operatorName',
        accessorFn: (row) => row.operatorName ?? '-',
        header: 'Opérateur.ice',
        meta: { defaultVisible: false },
    },
    {
        id: 'siteName',
        accessorFn: (row) => getBundleSiteNames(row),
        header: 'Site',
        meta: { defaultVisible: false },
    },
    {
        accessorKey: 'type',
        header: 'Type',
        meta: { defaultVisible: true },
        cell: ({ row }) => <BundleTypeTag type={row.original.type} />,
    },
    {
        accessorKey: 'containerType',
        header: 'Container',
        meta: { defaultVisible: false },
        cell: ({ row }) => <ContainerTypeTag type={row.original.containerType} />,
    },
    {
        id: 'boxes',
        header: 'Caisses',
        meta: { defaultVisible: true },
        cell: ({ row }) => (
            <Boxes>
                {[...row.original.boxes]
                    .sort((boxA, boxB) => (boxA.position || 0) - (boxB.position || 0))
                    .map(({ _id, label, deliveryId, position }) => (
                        <BoxChip key={_id}>
                            {position !== null && position !== undefined ? (
                                <PositionBadge title="Position dans le bundle">#{position}</PositionBadge>
                            ) : null}
                            <DetailLink path="delivery" value={deliveryId} name={label} shouldWrap />
                        </BoxChip>
                    ))}
            </Boxes>
        ),
    },
    {
        id: 'stats',
        header: 'Stats',
        meta: { defaultVisible: true },
        cell: ({ row }) => {
            const {
                numberOfReferences,
                numberOfUnits,
                operatorName,
                preparationDates: { start, end },
                preparationDuration,
                stateHistory,
            } = row.original;
            const hasBeenPaused = stateHistory.some((entry) => entry.state === BundleState.Paused);
            return (
                <StatsCell>
                    <StatLine>
                        <StatValue>{numberOfReferences}</StatValue>
                        <StatLabel>réfs</StatLabel>
                        <StatSeparator>·</StatSeparator>
                        <StatValue>{numberOfUnits}</StatValue>
                        <StatLabel>unités</StatLabel>
                    </StatLine>
                    <StatMetaLine>
                        <StatLabel>Op.</StatLabel>
                        <StatMetaValue>{operatorName ?? '-'}</StatMetaValue>
                    </StatMetaLine>
                    <StatMetaLine>
                        <StatMetaValue>
                            {start || '?'}&nbsp;&rarr;&nbsp;{end || '?'}
                        </StatMetaValue>
                        <StatSeparator>·</StatSeparator>
                        <StatMetaValue>{formatPreparationDuration(preparationDuration)}</StatMetaValue>
                    </StatMetaLine>
                    {hasBeenPaused ? <PausedWarning>A été "paused"</PausedWarning> : null}
                </StatsCell>
            );
        },
    },
    {
        accessorKey: 'state',
        header: 'Statut',
        meta: { defaultVisible: true },
        cell: ({ row }) => <StatusTag state={row.original.state} />,
    },
];

const Boxes = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const BoxChip = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const PositionBadge = styled.span`
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 700;
    color: ${({ theme }) => theme.ctaPrimaryColor};
`;

const PausedWarning = styled.div`
    font-size: 12px;
    color: ${({ theme }) => theme.warningColor};
`;

const StatsCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    white-space: nowrap;
`;

const StatLine = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;
`;

const StatMetaLine = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-top: 1px;
`;

const StatValue = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${({ theme }) => theme.textColor};
`;

const StatLabel = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const StatMetaValue = styled.span`
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
`;

const StatSeparator = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
`;
